import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../common/types';
import { useUserTokenDecoded } from '../../../redux/slices/appInfo/hooks';
import { useWorkspaces } from '../../workspaces/useWorkspaces';
import { workspaceInitials } from '../../workspaces/workspaceInitials';
import { oauthConsentMessages as messages } from './oauthConsentMessages';
import { withBrandWord } from '../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole consent page; each part uses its own slice of it */
import styles from './OAuthConsentView.module.scss';
/* eslint-enable css-modules/no-unused-class */

// The hue of a workspace mark comes from the licence id, as in the workspace switcher.
const markHues = [styles.hue0, styles.hue1, styles.hue2, styles.hue3, styles.hue4, styles.hue5];

/**
 * Which workspace and which user the app will act for (design: oauth-consent a1, `.oc-acct`).
 * The approval binds the token to the workspace that is open now (`oauthConsent.approve` reads
 * `ctx.workspace`). The name comes from `workspaces.list`, the email from the session. While the
 * list loads, or when it fails, the box shows the email alone: the list is not needed to approve.
 */
const OAuthConsentAccountComponent: React.FC = () => {
  const intl = useIntl();
  const email = useUserTokenDecoded()?.email ?? '';
  const { active, workspaces } = useWorkspaces();
  if (active === null && email === '') {
    return null;
  }
  const signedInAs = email === '' ? null : intl.formatMessage(messages.signedInAs, { email });
  return (
    <div className={styles.section}>
      <div className={styles.label}>{intl.formatMessage(messages.accountLabel)}</div>
      <div className={styles.account}>
        {active !== null && (
          <span className={clsx(styles.workspaceMark, markHues[active.licenseId % markHues.length])} aria-hidden="true">
            {workspaceInitials(active.name)}
          </span>
        )}
        <div className={styles.accountText}>
          {active !== null && <b className={styles.accountName}>{active.name}</b>}
          {signedInAs !== null && (
            <span className={active === null ? styles.accountName : styles.accountEmail} data-clarity-mask="true">
              {signedInAs}
            </span>
          )}
        </div>
      </div>
      {workspaces.length > 1 && (
        <p className={styles.hint}>{withBrandWord(intl.formatMessage(messages.otherWorkspace))}</p>
      )}
    </div>
  );
};

export const OAuthConsentAccount = TypedMemo(OAuthConsentAccountComponent);
